import React from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { BarChart3, Clock, CheckCircle, Users, Store, CreditCard, Download, FileSpreadsheet, ShoppingCart, Video } from 'lucide-react';
import { useCurrency } from '../../contexts/CurrencyContext';
import { useTranslation } from 'react-i18next';

const SellerStatsTab = ({ callStats, boothVisitors, eventAttendees, listPricing, activeBooth, downloadList, handlePurchaseList, openUpgradeModal }) => {
  const { formatPrice } = useCurrency();
  const { t } = useTranslation();

  return (
    <div className="space-y-6">
      {/* Call Statistics */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><BarChart3 className="h-5 w-5" /> {t('dashboard.video_call_stats')}</CardTitle>
          <CardDescription>{t('dashboard.video_call_stats_desc')}</CardDescription>
        </CardHeader>
        <CardContent>
          {callStats ? (
            <div className="space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 rounded-lg p-4"><p className="text-sm text-blue-600 font-medium">{t('dashboard.total_calls')}</p><p className="text-3xl font-bold text-blue-700">{callStats.total_calls}</p></div>
                <div className="bg-green-50 rounded-lg p-4"><p className="text-sm text-green-600 font-medium">{t('dashboard.completed')}</p><p className="text-3xl font-bold text-green-700">{callStats.completed_calls}</p></div>
                <div className="bg-amber-50 rounded-lg p-4"><p className="text-sm text-amber-600 font-medium">{t('dashboard.pending')}</p><p className="text-3xl font-bold text-amber-700">{callStats.pending_calls}</p></div>
                <div className="bg-red-50 rounded-lg p-4"><p className="text-sm text-red-600 font-medium">{t('dashboard.abandoned') || 'Abandoned'}</p><p className="text-3xl font-bold text-red-700">{callStats.abandoned_calls}</p></div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="border rounded-lg p-4"><div className="flex items-center gap-2 text-slate-600 mb-1"><CheckCircle className="h-4 w-4" /><span className="text-sm">Completion Rate</span></div><p className="text-2xl font-bold">{callStats.completion_rate}%</p></div>
                <div className="border rounded-lg p-4"><div className="flex items-center gap-2 text-slate-600 mb-1"><Clock className="h-4 w-4" /><span className="text-sm">Avg. Duration</span></div><p className="text-2xl font-bold">{Math.floor(callStats.average_duration_seconds / 60)}m {callStats.average_duration_seconds % 60}s</p></div>
                <div className="border rounded-lg p-4"><div className="flex items-center gap-2 text-slate-600 mb-1"><Clock className="h-4 w-4" /><span className="text-sm">Total Time</span></div><p className="text-2xl font-bold">{Math.floor(callStats.total_duration_seconds / 3600)}h {Math.floor((callStats.total_duration_seconds % 3600) / 60)}m</p></div>
              </div>
              {Object.keys(callStats.rep_stats || {}).length > 0 && (
                <div>
                  <h4 className="font-semibold mb-3">{t('dashboard.representative_performance')}</h4>
                  <Table>
                    <TableHeader><TableRow><TableHead>{t('dashboard.rep')}</TableHead><TableHead>{t('dashboard.total_calls')}</TableHead><TableHead>{t('dashboard.completed')}</TableHead><TableHead>{t('dashboard.avg_duration')}</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {Object.entries(callStats.rep_stats).map(([name, stats]) => (
                        <TableRow key={name}><TableCell className="font-medium">{name}</TableCell><TableCell>{stats.calls}</TableCell><TableCell>{stats.completed}</TableCell><TableCell>{Math.floor(stats.total_duration / 60)}m {stats.total_duration % 60}s</TableCell></TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
              {callStats.recent_calls?.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-3">{t('dashboard.recent_calls')}</h4>
                  <Table>
                    <TableHeader><TableRow><TableHead>{t('dashboard.attendee')}</TableHead><TableHead>{t('dashboard.rep')}</TableHead><TableHead>{t('dashboard.status')}</TableHead><TableHead>{t('dashboard.duration')}</TableHead><TableHead>{t('dashboard.date')}</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {callStats.recent_calls.slice(0, 10).map((call) => (
                        <TableRow key={call.id}>
                          <TableCell>{call.buyer_name}</TableCell><TableCell>{call.rep_name || '-'}</TableCell>
                          <TableCell><Badge className={call.status === 'completed' ? 'bg-green-100 text-green-700' : call.status === 'abandoned' ? 'bg-red-100 text-red-700' : call.status === 'on_call' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}>{call.status}</Badge></TableCell>
                          <TableCell>{call.duration_seconds ? `${Math.floor(call.duration_seconds / 60)}m ${call.duration_seconds % 60}s` : '-'}</TableCell>
                          <TableCell>{new Date(call.created_at).toLocaleDateString()}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500"><BarChart3 className="h-12 w-12 mx-auto mb-3 opacity-50" /><p>No call statistics available yet.</p><p className="text-sm">Statistics will appear once you start handling video calls.</p></div>
          )}
        </CardContent>
      </Card>

      {/* Booth Visitors */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5" /> {t('dashboard.booth_visitors')}
              {activeBooth?.tier === 'basic' && <Badge className="bg-orange-100 text-orange-700 ml-2">Premium Feature</Badge>}
            </div>
            {boothVisitors?.visitors?.length > 0 && !boothVisitors?.tier_required && (
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => downloadList('visitors', 'csv')}><Download className="h-4 w-4 mr-1" /> CSV</Button>
                <Button size="sm" variant="outline" onClick={() => downloadList('visitors', 'xlsx')}><FileSpreadsheet className="h-4 w-4 mr-1" /> Excel</Button>
              </div>
            )}
          </CardTitle>
          <CardDescription>{activeBooth?.tier === 'basic' ? t('dashboard.upgrade_tier') : t('dashboard.booth_visitors_desc')}</CardDescription>
        </CardHeader>
        <CardContent>
          {boothVisitors?.tier_required ? (
            <div className="text-center py-8 bg-orange-50 border border-orange-200 rounded-lg">
              <Users className="h-12 w-12 text-orange-400 mx-auto mb-3" /><p className="text-orange-800 font-medium">{boothVisitors.message}</p>
              <Button variant="outline" className="mt-3 border-orange-400 text-orange-700 hover:bg-orange-100" onClick={openUpgradeModal}><CreditCard className="h-4 w-4 mr-2" />{t('dashboard.upgrade_tier')}</Button>
            </div>
          ) : boothVisitors?.visitors?.length > 0 ? (
            <div className="space-y-4">
              <div className="flex items-center gap-4 mb-4"><div className="bg-blue-50 rounded-lg px-4 py-2"><p className="text-sm text-blue-600">Total Visitors</p><p className="text-2xl font-bold text-blue-700">{boothVisitors.total_visitors}</p></div></div>
              <Table>
                <TableHeader><TableRow><TableHead>{t('dashboard.name')}</TableHead><TableHead>{t('dashboard.company')}</TableHead><TableHead>{t('dashboard.interactions')}</TableHead><TableHead>{t('dashboard.completed_calls')}</TableHead><TableHead>{t('dashboard.last_visit')}</TableHead></TableRow></TableHeader>
                <TableBody>
                  {boothVisitors.visitors.slice(0, 20).map((visitor) => (
                    <TableRow key={visitor.id}>
                      <TableCell><div><p className="font-medium">{visitor.name}</p><p className="text-xs text-slate-500">{visitor.email}</p></div></TableCell>
                      <TableCell>{visitor.company || '-'}</TableCell><TableCell>{visitor.total_interactions}</TableCell><TableCell>{visitor.completed_calls}</TableCell>
                      <TableCell>{visitor.last_interaction ? new Date(visitor.last_interaction).toLocaleDateString() : '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500"><Users className="h-12 w-12 mx-auto mb-3 opacity-50" /><p>No visitors yet.</p><p className="text-sm">Visitors will appear once attendees interact with your booth.</p></div>
          )}
        </CardContent>
      </Card>

      {/* Event Attendees List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Store className="h-5 w-5" /> {t('dashboard.all_event_attendees')}
              {activeBooth?.tier !== 'elite' && !listPricing?.attendee_list_purchased && (
                <Badge className="bg-purple-100 text-purple-700 ml-2">{listPricing?.attendee_list_price > 0 ? formatPrice(listPricing?.attendee_list_price, true) : 'Elite Feature'}</Badge>
              )}
              {listPricing?.attendee_list_purchased && <Badge className="bg-green-100 text-green-700 ml-2">{t('dashboard.completed')}</Badge>}
            </div>
            {(listPricing?.attendee_list_purchased || activeBooth?.tier === 'elite') && eventAttendees?.attendees?.length > 0 && (
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => downloadList('attendees', 'csv')}><Download className="h-4 w-4 mr-1" /> CSV</Button>
                <Button size="sm" variant="outline" onClick={() => downloadList('attendees', 'xlsx')}><FileSpreadsheet className="h-4 w-4 mr-1" /> Excel</Button>
              </div>
            )}
          </CardTitle>
          <CardDescription>
            {activeBooth?.tier === 'elite' || listPricing?.attendee_list_purchased ? t('dashboard.all_event_attendees_desc')
              : listPricing?.attendee_list_price > 0 ? `${t('dashboard.purchase_list')} - ${formatPrice(listPricing?.attendee_list_price, true)}`
              : t('dashboard.all_event_attendees_desc')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {activeBooth?.tier !== 'elite' && !listPricing?.attendee_list_purchased && listPricing?.attendee_list_price > 0 ? (
            <div className="text-center py-8 bg-purple-50 border border-purple-200 rounded-lg">
              <ShoppingCart className="h-12 w-12 text-purple-400 mx-auto mb-3" /><p className="text-purple-800 font-medium">{t('dashboard.purchase_list')}</p>
              <p className="text-purple-600 text-sm mt-2 mb-4">Get contact details for {listPricing?.attendee_count || 0} registered attendees</p>
              <Button onClick={() => handlePurchaseList('attendee_list')} className="bg-purple-600 hover:bg-purple-700"><CreditCard className="h-4 w-4 mr-2" />Purchase for {formatPrice(listPricing?.attendee_list_price, true)}</Button>
            </div>
          ) : eventAttendees?.tier_required && activeBooth?.tier !== 'elite' && !listPricing?.attendee_list_purchased ? (
            <div className="text-center py-8 bg-purple-50 border border-purple-200 rounded-lg">
              <Store className="h-12 w-12 text-purple-400 mx-auto mb-3" /><p className="text-purple-800 font-medium">{eventAttendees.message}</p>
              <p className="text-purple-600 text-sm mt-2">Elite exhibitors can view all event attendees to plan outreach.</p>
            </div>
          ) : eventAttendees?.attendees?.length > 0 ? (
            <div className="space-y-4">
              <div className="flex items-center gap-4 mb-4">
                <div className="bg-purple-50 rounded-lg px-4 py-2"><p className="text-sm text-purple-600">Total Attendees</p><p className="text-2xl font-bold text-purple-700">{eventAttendees.total_attendees}</p></div>
                <div className="bg-green-50 rounded-lg px-4 py-2"><p className="text-sm text-green-600">Interacted</p><p className="text-2xl font-bold text-green-700">{eventAttendees.interacted_count}</p></div>
                <div className="bg-slate-50 rounded-lg px-4 py-2"><p className="text-sm text-slate-600">Not Yet Engaged</p><p className="text-2xl font-bold text-slate-700">{eventAttendees.total_attendees - eventAttendees.interacted_count}</p></div>
              </div>
              <Table>
                <TableHeader><TableRow><TableHead>{t('dashboard.name')}</TableHead><TableHead>{t('dashboard.company')}</TableHead><TableHead>{t('dashboard.email')}</TableHead><TableHead>{t('dashboard.status')}</TableHead><TableHead>{t('dashboard.registered')}</TableHead></TableRow></TableHeader>
                <TableBody>
                  {eventAttendees.attendees.slice(0, 50).map((attendee) => (
                    <TableRow key={attendee.id}>
                      <TableCell className="font-medium">{attendee.name}</TableCell><TableCell>{attendee.company || '-'}</TableCell><TableCell className="text-sm">{attendee.email}</TableCell>
                      <TableCell>{attendee.has_interacted ? <Badge className="bg-green-100 text-green-700"><CheckCircle className="h-3 w-3 mr-1" /> Engaged</Badge> : <Badge className="bg-slate-100 text-slate-600">Not Yet</Badge>}</TableCell>
                      <TableCell>{attendee.registered_at ? new Date(attendee.registered_at).toLocaleDateString() : '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500"><Store className="h-12 w-12 mx-auto mb-3 opacity-50" /><p>No attendees registered for this event yet.</p></div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SellerStatsTab;
